import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import Iconify from 'src/components/Iconify';
import MetricCard from 'src/sections/@dashboard/marketing/components/MetricCard';
import MktLoading from 'src/sections/@dashboard/marketing/components/MktLoading';
import { CHART_TOOLTIP_STYLE } from 'src/design/tokens';
import { MKT_ANALYTICS_DESIGN as d } from './MktAnalytics.design';

const PRIMARY = '#1877F2';
const SECONDARY = '#42A5F5';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const dt = new Date(dateStr);
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

function formatNumber(num) {
  if (num == null) return '—';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}

function truncate(str, len = 80) {
  if (!str) return '—';
  return str.length > len ? `${str.slice(0, len)}...` : str;
}

export default function FacebookTab({ data }) {
  const { t } = useTranslation('marketing');

  if (!data) {
    return <MktLoading text={t('analytics.facebook.loading')} />;
  }

  const { summary, daily, demographics, top_posts, page_fans_by_day } = data;
  // A Meta descontinuou page_fans_gender_age em 14/03/2024; sem dado, o
  // grafico some em vez de renderizar vazio.
  const hasAgeGender = Boolean(demographics?.age_gender?.length);

  const dailyFormatted = (daily || []).map((row) => ({
    ...row,
    dateLabel: formatDate(row.date),
  }));

  const fansFormatted = (page_fans_by_day || []).map((row) => ({
    ...row,
    dateLabel: formatDate(row.date),
  }));

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Stats Grid */}
      <div className={d.stats.gridWide}>
        <MetricCard
          icon="eva:eye-outline"
          label="Page Views"
          value={formatNumber(summary?.page_views)}
          growth={summary?.page_views_growth_pct}
          color={PRIMARY}
        />
        {/* page_impressions morreu em 15/11/2025 -> page_media_view. O backend
            preenche a mesma chave; o rotulo passa a dizer o que o numero e. */}
        <MetricCard
          icon="eva:activity-outline"
          label={t('analytics.facebook.mediaViews')}
          value={formatNumber(summary?.page_media_view ?? summary?.page_impressions)}
          growth={summary?.page_impressions_growth_pct}
          color={PRIMARY}
        />
        <MetricCard
          icon="eva:heart-outline"
          label={t('analytics.facebook.engagements')}
          value={formatNumber(summary?.post_engagements)}
          growth={summary?.post_engagements_growth_pct}
          color={PRIMARY}
        />
        {/* page_fans -> page_follows: a Meta unificou curtidas e seguidores. */}
        <MetricCard
          icon="eva:people-outline"
          label={t('analytics.facebook.followers')}
          value={formatNumber(summary?.page_fans)}
          color={PRIMARY}
        />
        <MetricCard
          icon="eva:person-add-outline"
          label={t('analytics.facebook.newFollowers')}
          value={formatNumber(summary?.page_fans_add)}
          growth={summary?.page_fans_add_growth_pct}
          color={PRIMARY}
        />
      </div>

      {/* Impressions Chart */}
      <div className={d.charts.container}>
        <h3 className={d.charts.title}>
          <Iconify icon="eva:trending-up-outline" width={16} className="inline mr-1.5 text-[#1877F2]" />
          {t('analytics.facebook.charts.impressionsOverTime')}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={dailyFormatted}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
            <Tooltip {...CHART_TOOLTIP_STYLE} />
            <Legend />
            <Line
              type="monotone"
              dataKey="page_impressions"
              name={t('analytics.facebook.mediaViews')}
              stroke={PRIMARY}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="page_impressions_unique"
              name={t('analytics.facebook.charts.uniqueReach')}
              stroke={SECONDARY}
              strokeWidth={2}
              dot={false}
              strokeDasharray="5 5"
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Page Fans Evolution */}
      <div className={d.charts.container}>
        <h3 className={d.charts.title}>
          <Iconify icon="eva:people-outline" width={16} className="inline mr-1.5 text-[#1877F2]" />
          {t('analytics.facebook.charts.fansEvolution')}
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={fansFormatted}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
            <Tooltip {...CHART_TOOLTIP_STYLE} />
            <Line
              type="monotone"
              dataKey="fans"
              name="Fans"
              stroke={PRIMARY}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5 }}
              fill={PRIMARY}
              fillOpacity={0.1}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Post Engagement Breakdown */}
      <div className={d.charts.container}>
        <h3 className={d.charts.title}>
          <Iconify icon="eva:heart-outline" width={16} className="inline mr-1.5 text-[#1877F2]" />
          {t('analytics.facebook.charts.engagementByDay')}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={dailyFormatted}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
            <Tooltip {...CHART_TOOLTIP_STYLE} />
            <Legend />
            <Bar dataKey="reactions" name={t('analytics.facebook.interactions.reactions')} fill={PRIMARY} radius={[3, 3, 0, 0]} />
            <Bar dataKey="comments" name={t('analytics.facebook.interactions.comments')} fill={SECONDARY} radius={[3, 3, 0, 0]} />
            <Bar dataKey="shares" name={t('analytics.facebook.interactions.shares')} fill="#90CAF9" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Demographics */}
      <div className={hasAgeGender ? d.charts.grid2 : ''}>
        {/* Idade e genero: a Meta descontinuou page_fans_gender_age em
            14/03/2024 e nao ha substituto. Renderizar o grafico vazio passaria
            a impressao de "esta Pagina nao tem publico" — some com ele. */}
        {hasAgeGender && (
          <div className={d.charts.container}>
            <h3 className={d.charts.title}>
              <Iconify icon="eva:bar-chart-outline" width={16} className="inline mr-1.5 text-[#1877F2]" />
              {t('analytics.facebook.demographics.title')}
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={demographics.age_gender} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis dataKey="range" type="category" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} width={60} />
                <Tooltip {...CHART_TOOLTIP_STYLE} />
                <Legend />
                <Bar dataKey="male" name={t('analytics.demographics.male')} fill={PRIMARY} radius={[0, 3, 3, 0]} />
                <Bar dataKey="female" name={t('analytics.demographics.female')} fill="#E91E63" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top Cities */}
        <div className={d.table.container}>
          <div className={d.table.header}>
            <h3 className={d.table.headerTitle}>
              <Iconify icon="eva:pin-outline" width={16} className="inline mr-1.5 text-[#1877F2]" />
              {t('analytics.facebook.topCities')}
            </h3>
          </div>
          <div className="max-h-[320px] overflow-y-auto">
            {(demographics?.cities || []).map((city, idx) => (
              <div key={idx} className={d.table.row}>
                <span className="text-xs font-medium text-gray-400 w-6">{idx + 1}</span>
                <span className={`${d.table.cell} flex-1`}>{city.name}</span>
                <span className="text-sm font-semibold text-gray-900">{formatNumber(city.count)}</span>
              </div>
            ))}
            {(!demographics?.cities || demographics.cities.length === 0) && (
              <div className="px-5 py-8 text-center text-sm text-gray-400">{t('analytics.facebook.noCityData')}</div>
            )}
          </div>
        </div>
      </div>

      {/* Top Posts Table */}
      <div className={d.table.container}>
        <div className={d.table.header}>
          <h3 className={d.table.headerTitle}>
            <Iconify icon="eva:file-text-outline" width={16} className="inline mr-1.5 text-[#1877F2]" />
            {t('analytics.overview.topPosts')}
          </h3>
          <span className={d.table.cellMuted}>{t('analytics.facebook.postCount', { count: (top_posts || []).length })}</span>
        </div>

        {/* Table Header */}
        <div className="px-5 py-2.5 border-b border-gray-100 flex items-center bg-gray-50/60">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex-1">{t('analytics.facebook.table.message')}</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 text-right">{t('analytics.instagram.metrics.impressions')}</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-20 text-right">{t('analytics.facebook.interactions.reactions')}</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 text-right">{t('analytics.facebook.interactions.comments')}</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 text-right">{t('analytics.facebook.interactions.sharesShort')}</span>
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-20 text-right">{t('analytics.facebook.table.clicks')}</span>
        </div>

        {/* Table Rows */}
        <div className="max-h-[400px] overflow-y-auto">
          {(top_posts || []).map((post, idx) => (
            <div key={post.external_id || idx} className={`${d.table.row} gap-2`}>
              <div className="flex-1 flex items-center gap-3 min-w-0">
                {post.thumbnail ? (
                  <img
                    src={post.thumbnail}
                    alt=""
                    className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Iconify icon="eva:image-outline" width={18} className="text-blue-300" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 truncate">{truncate(post.message)}</p>
                  <p className={d.table.cellMuted}>
                    {post.type} &middot; {post.published_at ? new Date(post.published_at).toLocaleDateString('pt-BR') : '—'}
                  </p>
                </div>
              </div>
              <span className="text-sm font-medium text-gray-900 w-24 text-right">{formatNumber(post.impressions)}</span>
              <span className="text-sm text-gray-700 w-20 text-right">{formatNumber(post.reactions)}</span>
              <span className="text-sm text-gray-700 w-24 text-right">{formatNumber(post.comments)}</span>
              <span className="text-sm text-gray-700 w-24 text-right">{formatNumber(post.shares)}</span>
              <span className="text-sm text-gray-700 w-20 text-right">{formatNumber(post.clicks)}</span>
            </div>
          ))}
          {(!top_posts || top_posts.length === 0) && (
            <div className="px-5 py-12 text-center">
              <Iconify icon="eva:file-text-outline" width={32} className="text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">{t('analytics.noPosts')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
